/**
 * Dynamic Remote Loader for Module Federation
 *
 * Carga componentes React desde URLs remotas en runtime.
 * Las URLs no estan hardcodeadas - vienen de Firestore via entitlements API.
 *
 * Flujo:
 * 1. Inyecta <script> tag con el remoteEntry.js del app remoto
 * 2. Inicializa el container con las shared dependencies del host
 * 3. Obtiene la factory del modulo expuesto (./App)
 * 4. Retorna un React.lazy component listo para renderizar
 */

import { lazy, ComponentType } from 'react'
import type { AppProps } from '@summafx/types'
import { perf } from '@/lib/perf'

export interface RemoteAppConfig {
  remoteUrl: string
  scope: string
  module: string
}

const containerCache = new Map<string, unknown>()

/**
 * Valida que la URL sea HTTPS en producción o localhost en desarrollo.
 * No se usa allowlist hardcodeado — las URLs vienen del entitlements API
 * (Firestore controlado por admins), que ya es la fuente de verdad.
 */
function validateRemoteUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.hostname === 'localhost'
  } catch {
    return false
  }
}

async function loadRemoteContainer(url: string, scope: string): Promise<unknown> {
  const cached = containerCache.get(url)
  if (cached) return cached

  if (!validateRemoteUrl(url)) {
    throw new Error(`[RemoteLoader] Blocked: origin not allowed for ${url}`)
  }

  // Vite federation generates ES module remoteEntry.js (not global script).
  // Use dynamic import() to load it. webpackIgnore tells webpack to leave this
  // as a runtime import instead of trying to bundle it.
  const endLoad = perf.span(`focus:remote:load-container:${scope}`)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const container = await (new Function('url', 'return import(url)')(url)) as any
  endLoad()

  if (!container || (!container.get && !container.default?.get)) {
    throw new Error(`[RemoteLoader] Container "${scope}" has no "get" method: ${url}`)
  }

  const actualContainer = container.default || container
  containerCache.set(url, actualContainer)
  return actualContainer
}

async function loadRemoteModule<T>(config: RemoteAppConfig): Promise<T> {
  const endModule = perf.span(`focus:remote:load-module:${config.scope}`)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const container = await loadRemoteContainer(config.remoteUrl, config.scope) as any

  // Vite federation: init() may be a no-op but call it for compatibility
  if (container.init) {
    try { await container.init({}) } catch { /* already initialized */ }
  }

  const factory = await container.get(config.module)
  const mod = factory()
  endModule()

  // Vite federation unwraps default when the module ONLY has standard exports
  // (default, __esModule, Module, _export_sfc). In that case factory() returns
  // the component directly instead of { default: Component }.
  // React.lazy requires { default: Component }, so we wrap it if needed.
  if (typeof mod === 'function' || (typeof mod === 'object' && mod && !('default' in mod))) {
    return { default: mod } as T
  }

  return mod
}

/**
 * Crea un React.lazy component para una app remota.
 * Se carga desde la URL al primer render, con cache automatico.
 */
export function createRemoteApp(config: RemoteAppConfig) {
  return lazy<ComponentType<AppProps>>(() =>
    loadRemoteModule<{ default: ComponentType<AppProps> }>(config)
  )
}
